<template>
  <div>
    <RoomSelection />
    <!-- Below-fold sections wrapped in `.cv-auto` so the browser skips
         paint until they enter the viewport. PSI 2026-05-02 19:38 trace
         showed LCP=12.9s on mobile despite hero poster preloaded — gap
         between FCP (6.8s) and LCP came from lazy-component hydration
         briefly becoming the largest paint, repeatedly invalidating the
         hero's LCP claim. content-visibility:auto pins the LCP candidate
         to the hero <picture>: below-fold paints are deferred to scroll
         and don't compete during the LCP measurement window. -->
    <section id="apartments" class="cv-auto relative py-10 md:py-16 overflow-hidden scroll-mt-24">
      <LazyApartmentApartmentsListMain />
      <div
        aria-hidden="true"
        class="pointer-events-none absolute -bottom-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-amber-400/5 blur-3xl dark:bg-amber-400/10"
      />
    </section>
    <div class="cv-auto">
      <LazyPricesPriceTable variant="summary" />
    </div>
    <!-- Entity-defining "About Dreamland Oasis" block. Plain SSR prose (no
         hydration cost) so AI engines (AIO/ChatGPT/Perplexity) can cite a
         single self-contained passage — the #1 GEO gap from the 2026-06-03
         audit — styled to match the site's section design (amber-hairline
         eyebrow, display-italic heading, ambient glow, scroll reveal). The
         white band alternates against the slate-50 Prices/Testimonials
         neighbours; .cv-auto defers below-fold paint. -->
    <div class="cv-auto">
      <section
        aria-labelledby="about-resort-heading"
        class="relative overflow-hidden py-10 md:py-16 bg-white dark:bg-slate-900"
      >
        <div
          class="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent"
          aria-hidden="true"
        />
        <div
          class="pointer-events-none absolute -top-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-amber-400/5 blur-3xl dark:bg-amber-400/10"
          aria-hidden="true"
        />
        <div data-reveal class="container relative">
          <div class="text-center max-w-2xl mx-auto">
            <div
              class="flex items-center justify-center gap-3 font-body text-xs uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500"
            >
              <span class="inline-block h-px w-8 bg-amber-400" aria-hidden="true" />
              <span>{{ $t('aboutResort.heading') }}</span>
              <span class="inline-block h-px w-8 bg-amber-400" aria-hidden="true" />
            </div>
            <h2
              id="about-resort-heading"
              class="mt-4 font-display italic text-3xl sm:text-4xl md:text-5xl text-balance text-slate-900 dark:text-white"
            >
              {{ $t('aboutResort.heading2') }}
            </h2>
          </div>
          <p
            class="mx-auto mt-6 max-w-3xl text-base md:text-lg leading-relaxed text-slate-600 dark:text-slate-300"
            data-speakable
          >
            {{ $t('aboutResort.body') }}
          </p>
        </div>
      </section>
    </div>
    <div class="cv-auto">
      <LazyTestimonialsCarousel />
    </div>
  </div>
</template>

<script setup>
// Below-fold components are referenced via Nuxt's <Lazy*> auto-import prefix
// so each lands in its own JS chunk and is loaded only when needed. Hero
// (RoomSelection) stays eager — it owns the LCP candidate.
import RoomSelection from "~/components/mainpage/RoomSelection.vue";
import { buildLodgingBusinessLd } from "~/utils/lodgingBusinessSchema";
import { buildReviewLdNodes } from "~/utils/reviewsSchema";
import { testimonials } from "~/data/testimonials";

const { t, locale } = useI18n();
const route = useRoute();
const config = useRuntimeConfig();

// PricesPriceTable wraps its body in [data-reveal]; without an observer the
// container stays at opacity:0. CSS in main.css fades it in on .is-visible.
// Tuned to trigger ~240px before the section enters the viewport so the
// "Сезон 2026" block is already faded-in by the time the user scrolls onto it.
useReveal({ threshold: 0, rootMargin: "0px 0px 240px 0px" });

const ogImage = "https://georgiadream.ge/images/og/home.jpg";
const canonicalUrl = computed(() => `https://georgiadream.ge${route.path}`);
const ogLocale = computed(() =>
  locale.value === "ru" ? "ru_RU" : locale.value === "ka" ? "ka_GE" : "en_US"
);
const ogLocaleAlternate = computed(() =>
  ["en_US", "ru_RU", "ka_GE"].filter((l) => l !== ogLocale.value)
);

const { data: lodgingStats } = await useAsyncData(
  "lodging-stats",
  () => $fetch("/api/apartments/lodging-stats"),
  // Stats change only when prices or unit count change (rare). One fetch per
  // SSR is cheap; client-side navigation reuses the cached payload.
  { default: () => ({ priceRange: "$$", numberOfRooms: 13 }) }
);

const lodgingBaseLd = buildLodgingBusinessLd({
  email: config.public.contactEmail || "info@georgiadream.ge",
  whatsappNumber: config.public.whatsappNumber || "+995505556117",
  sameAs: [
    config.public.faceBookUrl,
    config.public.instagramUrl,
    config.public.telegramLink,
  ].filter((u) => typeof u === "string" && u.length > 0),
  locale: locale.value,
  priceRange: lodgingStats.value?.priceRange,
  numberOfRooms: lodgingStats.value?.numberOfRooms,
});

// Merge per-review nodes into the LodgingBusiness graph so each guest quote on
// the testimonials carousel gets a Review snippet eligible for SERP rendering
// (review stars + author + date). Quotes are emitted in their original
// language, matching what the guest wrote on Airbnb.
const lodgingLd = {
  ...lodgingBaseLd,
  "@graph": [
    ...lodgingBaseLd["@graph"],
    ...buildReviewLdNodes({ testimonials }),
  ],
};

useHead({
  // The default layout adds " · Dreamland Oasis Chakvi Apartments" to every
  // title via titleTemplate. The homepage title is already self-contained and
  // brand-rich, so opt out of the suffix here.
  titleTemplate: "%s",
  link: [{ rel: "canonical", href: canonicalUrl }],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify(lodgingLd),
    },
  ],
});

useSeoMeta({
  title: () => t("seo.home.title"),
  description: () => t("seo.home.description"),
  ogTitle: () => t("seo.home.title"),
  ogDescription: () => t("seo.home.description"),
  ogType: "website",
  ogUrl: canonicalUrl,
  ogImage,
  ogImageWidth: 1200,
  ogImageHeight: 630,
  ogImageAlt: () => t("seo.home.imageAlt"),
  ogSiteName: "Dreamland Oasis Chakvi",
  ogLocale,
  ogLocaleAlternate,
  twitterCard: "summary_large_image",
  twitterTitle: () => t("seo.home.title"),
  twitterDescription: () => t("seo.home.description"),
  twitterImage: ogImage,
  twitterImageAlt: () => t("seo.home.imageAlt"),
});
</script>
